import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, ListGroup, Spinner, Alert, InputGroup } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import "react-datepicker/dist/react-datepicker.css";
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';
import axios from 'axios';

registerLocale('pt-BR', ptBR);

// Define o estado inicial dos horários fora do componente para que seja uma constante.
// Isso define um padrão sensato de segunda a sexta, com fins de semana inativos.
const initialHorariosState = {
    dom: { ativo: false, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    seg: { ativo: true, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    ter: { ativo: true, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    qua: { ativo: true, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    qui: { ativo: true, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    sex: { ativo: true, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
    sab: { ativo: true, manha_inicio: '08:00', manha_fim: '12:00', tarde_inicio: '13:00', tarde_fim: '18:00', noite_inicio: '18:30', noite_fim: '20:00' },
};

// Mapeamento de dias da semana, definido fora do componente para ser constante.
const diasDaSemana = {
    seg: 'Segunda', ter: 'Terça', qua: 'Quarta', qui: 'Quinta', sex: 'Sexta', sab: 'Sábado', dom: 'Domingo'
};

const AgendasConfigTab = () => {
    const { currentEntidade, setCurrentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();
    const [isLoading, setIsLoading] = useState(true); // Inicia como true para a busca inicial
    const [isSaving, setIsSaving] = useState(false);
    const [duracaoPadrao, setDuracaoPadrao] = useState(30); // Novo estado para a duração
    const [horarios, setHorarios] = useState(initialHorariosState);

    // State for holidays
    const [feriados, setFeriados] = useState([]);
    const [newFeriadoDate, setNewFeriadoDate] = useState(new Date());
    const [newFeriadoDesc, setNewFeriadoDesc] = useState('');

    // State for blocked days
    const [diasBloqueados, setDiasBloqueados] = useState([]);
    const [newBloqueioDate, setNewBloqueioDate] = useState(new Date());
    const [newBloqueioDesc, setNewBloqueioDesc] = useState('');

    useEffect(() => {
        const fetchAgendaConfig = async () => {
            if (!currentEntidade) return;
            setIsLoading(true);
            try {
                // BACKEND: This endpoint will need to be created.
                const response = await axios.get(`/api/entidades/${currentEntidade.id}/agenda-config`);
                
                const fetchedHorarios = response.data.horarios || {};
                // Cria um objeto de horários completo, mesclando os dados recebidos da API com o estado inicial.
                // Isso garante que, mesmo que a API retorne um objeto vazio ou com dias faltando,
                // o componente terá uma estrutura válida para todos os dias da semana, evitando erros de renderização.
                const completeHorarios = {};
                for (const diaKey in diasDaSemana) {
                    completeHorarios[diaKey] = {
                        ...initialHorariosState[diaKey], // Começa com os padrões para o dia
                        ...(fetchedHorarios[diaKey] || {}), // Sobrescreve com os dados da API, se existirem
                    };
                }
                setHorarios(completeHorarios);

                setDuracaoPadrao(response.data.duracao_padrao || 30); // Busca a duração
                setFeriados(response.data.feriados || []);
                setDiasBloqueados(response.data.diasBloqueados || []);
            } catch (error) {
                console.error("Erro ao buscar configurações da agenda:", error);
                showAlertModal('Erro', 'Não foi possível carregar as configurações da agenda.', 'danger');
                // Reset to default states in case of error
                setHorarios(initialHorariosState);
                setDuracaoPadrao(30);
                setFeriados([]);
                setDiasBloqueados([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAgendaConfig();
    }, [currentEntidade, showAlertModal]); // A dependência de diasDaSemana não é necessária pois é uma constante externa.

    const handleHorarioChange = (dia, campo, valor) => {
        setHorarios(prev => ({
            ...prev,
            [dia]: { ...prev[dia], [campo]: valor }
        }));
    };

    const handleSaveHorarios = async () => {
        if (!currentEntidade) {
            showAlertModal('Erro', 'Nenhuma entidade selecionada.', 'danger');
            return;
        }
        setIsSaving(true);

        // Formata os horários para o formato H:i antes de enviar
        const formattedHorarios = {};
        for (const diaKey in horarios) {
            const dia = horarios[diaKey];
            formattedHorarios[diaKey] = {
                ...dia,
                manha_inicio: dia.manha_inicio ? dia.manha_inicio.substring(0, 5) : null,
                manha_fim: dia.manha_fim ? dia.manha_fim.substring(0, 5) : null,
                tarde_inicio: dia.tarde_inicio ? dia.tarde_inicio.substring(0, 5) : null,
                tarde_fim: dia.tarde_fim ? dia.tarde_fim.substring(0, 5) : null,
                noite_inicio: dia.noite_inicio ? dia.noite_inicio.substring(0, 5) : null,
                noite_fim: dia.noite_fim ? dia.noite_fim.substring(0, 5) : null,
            };
        }

        try {
            // 2. A API agora retorna o objeto 'entidade' completo
            const response = await axios.post(`/api/entidades/${currentEntidade.id}/agenda-config`, {
                horarios: formattedHorarios,
                duracao_padrao: duracaoPadrao,
            });
            showAlertModal('Sucesso', 'Configurações de horário salvas com sucesso!', 'success');

            // 3. ATUALIZAÇÃO GLOBAL: Atualiza o contexto com a nova entidade retornada pela API.
            // Isso fará com que toda a aplicação (Dashboard, Consultório) veja os novos horários.
            setCurrentEntidade(response.data.entidade);

        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao salvar as configurações de horário.';
            showAlertModal('Erro', errorMessage, 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddFeriado = async () => {
        if (!newFeriadoDesc.trim()) {
            showAlertModal('Atenção', 'A descrição do feriado é obrigatória.', 'warning');
            return;
        }
        if (!currentEntidade) {
            showAlertModal('Erro', 'Nenhuma entidade selecionada.', 'danger');
            return;
        }
        setIsSaving(true);
        try {
            const response = await axios.post(`/api/entidades/${currentEntidade.id}/agenda-excecoes`, {
                data: newFeriadoDate.toISOString().split('T')[0], // Formato YYYY-MM-DD
                descricao: newFeriadoDesc,
                tipo: 'feriado',
            });
            // O backend retorna a chave 'feriado' no sucesso
            setFeriados(prev => [...prev, response.data.feriado].sort((a, b) => new Date(a.data) - new Date(b.data)));
            setNewFeriadoDesc('');
            showAlertModal('Sucesso', 'Feriado adicionado com sucesso!', 'success');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao adicionar feriado.';
            showAlertModal('Erro', errorMessage, 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveFeriado = async (feriadoId) => {
        if (!window.confirm('Tem certeza que deseja remover este feriado?')) return;
        
        setIsSaving(true);
        try {
            await axios.delete(`/api/agenda-excecoes/${feriadoId}`);
            setFeriados(prev => prev.filter(f => f.id !== feriadoId));
            showAlertModal('Sucesso', 'Feriado removido com sucesso!', 'success');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao remover feriado.';
            showAlertModal('Erro', errorMessage, 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddBloqueio = async () => {
        if (!newBloqueioDesc.trim()) {
            showAlertModal('Atenção', 'O motivo do bloqueio é obrigatório.', 'warning');
            return;
        }
        if (!currentEntidade) {
            showAlertModal('Erro', 'Nenhuma entidade selecionada.', 'danger');
            return;
        }
        setIsSaving(true);
        try {
            const response = await axios.post(`/api/entidades/${currentEntidade.id}/agenda-excecoes`, {
                data: newBloqueioDate.toISOString().split('T')[0],
                descricao: newBloqueioDesc,
                tipo: 'bloqueio',
            });
            // O backend retorna a chave 'dia_bloqueado' no sucesso
            setDiasBloqueados(prev => [...prev, response.data.dia_bloqueado].sort((a, b) => new Date(a.data) - new Date(b.data)));
            setNewBloqueioDesc('');
            showAlertModal('Sucesso', 'Dia bloqueado adicionado com sucesso!', 'success');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao adicionar dia bloqueado.';
            showAlertModal('Erro', errorMessage, 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveBloqueio = async (bloqueioId) => {
        if (!window.confirm('Tem certeza que deseja remover este dia bloqueado?')) return;

        setIsSaving(true);
        try {
            await axios.delete(`/api/agenda-excecoes/${bloqueioId}`);
            setDiasBloqueados(prev => prev.filter(b => b.id !== bloqueioId));
            showAlertModal('Sucesso', 'Dia bloqueado removido com sucesso!', 'success');
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao remover dia bloqueado.';
            showAlertModal('Erro', errorMessage, 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Header>
                    <Row className="align-items-center">
                        <Col><Card.Title className="mb-0">Horários de Atendimento Padrão</Card.Title></Col>
                        <Col md="auto">
                            <Form.Group as={Row} controlId="duracaoPadrao" className="mb-0">
                                <Form.Label column sm="auto" className="pe-2">Duração (15-60 min):</Form.Label>
                                <Col sm="auto" className="ps-0">
                                    <Form.Control 
                                        type="number" 
                                        value={duracaoPadrao}
                                        onChange={(e) => {
                                            // Limita a entrada a no máximo 2 caracteres.
                                            if (e.target.value.length <= 2) {
                                                setDuracaoPadrao(e.target.value);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const value = parseInt(e.target.value, 10);
                                            if (isNaN(value) || value < 15) {
                                                setDuracaoPadrao(15);
                                            } else if (value > 60) {
                                                setDuracaoPadrao(60);
                                            }
                                        }}
                                        style={{ width: '80px' }} />
                                </Col>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body>
                    {isLoading ? (
                        <div className="text-center"><Spinner animation="border" /></div>
                    ) : (
                        <>
                        {Object.entries(diasDaSemana).map(([diaKey, diaNome]) => (
                        <Row key={diaKey} className="align-items-center mb-2 border-bottom pb-2">
                            <Col md={2}>
                                <Form.Check 
                                    type="switch"
                                    id={`switch-${diaKey}`}
                                    label={diaNome}
                                    checked={horarios[diaKey].ativo}
                                    onChange={(e) => handleHorarioChange(diaKey, 'ativo', e.target.checked)}
                                />
                            </Col>
                            <Col md={10}>
                                <Row>
                                    <Col><Form.Control type="time" size="sm" value={horarios[diaKey].manha_inicio} onChange={(e) => handleHorarioChange(diaKey, 'manha_inicio', e.target.value)} disabled={!horarios[diaKey].ativo} /></Col>
                                    <Col><Form.Control type="time" size="sm" value={horarios[diaKey].manha_fim} onChange={(e) => handleHorarioChange(diaKey, 'manha_fim', e.target.value)} disabled={!horarios[diaKey].ativo} /></Col>
                                    <Col><Form.Control type="time" size="sm" value={horarios[diaKey].tarde_inicio} onChange={(e) => handleHorarioChange(diaKey, 'tarde_inicio', e.target.value)} disabled={!horarios[diaKey].ativo} /></Col>
                                    <Col><Form.Control type="time" size="sm" value={horarios[diaKey].tarde_fim} onChange={(e) => handleHorarioChange(diaKey, 'tarde_fim', e.target.value)} disabled={!horarios[diaKey].ativo} /></Col>
                                    <Col><Form.Control type="time" size="sm" value={horarios[diaKey].noite_inicio} onChange={(e) => handleHorarioChange(diaKey, 'noite_inicio', e.target.value)} disabled={!horarios[diaKey].ativo} /></Col>
                                    <Col><Form.Control type="time" size="sm" value={horarios[diaKey].noite_fim} onChange={(e) => handleHorarioChange(diaKey, 'noite_fim', e.target.value)} disabled={!horarios[diaKey].ativo} /></Col>
                                </Row>
                            </Col>
                        </Row>
                        ))}
                        <div className="text-end mt-3">
                            <Button variant="primary" onClick={handleSaveHorarios} disabled={isSaving}>
                                {isSaving ? <><Spinner as="span" size="sm" /> Salvando...</> : 'Salvar Horários'}
                            </Button>
                        </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Row>
                <Col md={6}>
                    <Card>
                        <Card.Header><Card.Title>Feriados</Card.Title></Card.Header>
                        <Card.Body>
                            <ListGroup>
                                {feriados.map(f => (
                                    <ListGroup.Item key={f.id} className="d-flex justify-content-between align-items-center">
                                        {new Date(f.data + 'T00:00:00').toLocaleDateString('pt-BR')} - {f.descricao}
                                        <Button variant="outline-danger" size="sm" onClick={() => handleRemoveFeriado(f.id)} disabled={isSaving}>&times;</Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                            <hr />
                            <h6>Adicionar Feriado</h6>
                            <InputGroup className="mb-3">
                                <DatePicker selected={newFeriadoDate} onChange={(date) => setNewFeriadoDate(date)} className="form-control" dateFormat="dd/MM/yyyy" locale="pt-BR" />
                                <Form.Control placeholder="Descrição (Ex: Natal)" value={newFeriadoDesc} onChange={(e) => setNewFeriadoDesc(e.target.value)} />
                                <Button variant="outline-success" onClick={handleAddFeriado} disabled={isSaving}>+</Button>
                            </InputGroup>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card>
                        <Card.Header><Card.Title>Dias Bloqueados</Card.Title></Card.Header>
                        <Card.Body>
                            <ListGroup>
                                {diasBloqueados.map(b => (
                                    <ListGroup.Item key={b.id} className="d-flex justify-content-between align-items-center">
                                        {new Date(b.data + 'T00:00:00').toLocaleDateString('pt-BR')} - {b.descricao}
                                        <Button variant="outline-danger" size="sm" onClick={() => handleRemoveBloqueio(b.id)} disabled={isSaving}>&times;</Button>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                            <hr />
                            <h6>Adicionar Bloqueio</h6>
                            <InputGroup className="mb-3">
                                <DatePicker selected={newBloqueioDate} onChange={(date) => setNewBloqueioDate(date)} className="form-control" dateFormat="dd/MM/yyyy" locale="pt-BR" />
                                <Form.Control placeholder="Motivo (Ex: Recesso)" value={newBloqueioDesc} onChange={(e) => setNewBloqueioDesc(e.target.value)} />
                                <Button variant="outline-success" onClick={handleAddBloqueio} disabled={isSaving}>+</Button>
                            </InputGroup>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default AgendasConfigTab;